'use client'

import { refreshTokenOutcome } from './accessToken'
import { logger } from '@/lib/utils/logger'

/**
 * The one place in the app that may force a token refresh.
 *
 * The refresh itself happens on the server: this asks GET /api/auth/token for
 * a fresh token (src/lib/auth/accessToken.ts), and the middleware refreshes
 * the HttpOnly session cookie if it is due.
 *
 * Three separate helpers used to call the browser's refreshSession() on any
 * 401 with no coordination. Each call rotates the refresh token, so parallel
 * callers raced each other, and a page whose queries all failed produced one
 * refresh per query per round-trip. One user reached 437 token requests and 27
 * rate limits in a single afternoon on 2026-08-28.
 *
 * Guarantees:
 *  - at most one refresh in flight, shared by every concurrent caller
 *  - a minimum gap between refreshes, so a burst cannot become a loop
 *  - a breaker that opens after MAX_FAILURES real failures and answers false
 *    without touching the network until the window passes
 *
 * A transient failure (429, 5xx, network, abort) does not count against the
 * breaker: it says nothing about the session, and refusing to retry it would
 * strand a user whose connection blipped.
 */

/**
 * What the last completed refresh established about the session.
 *
 *  - `recovered`   – a valid session is in place right now
 *  - `rejected`    – GoTrue rejected the refresh token; the session is dead
 *  - `unavailable` – no verdict: a transient failure, the throttle, or an open
 *                    breaker. Says NOTHING about the session, so a caller must
 *                    never log the user out on it.
 *
 * The boolean `recoverSession()` collapses the last two into `false`, which is
 * the right answer for "should I retry my query" but the wrong one for "should
 * I clear the session". Callers that log a user out must read the outcome.
 */
export type SessionRecoveryOutcome = 'recovered' | 'rejected' | 'unavailable'

const MIN_INTERVAL_MS = 5_000
const MAX_FAILURES = 3
const BREAKER_WINDOW_MS = 60_000

let inFlight: Promise<SessionRecoveryOutcome> | null = null
let lastAttemptAt = 0
let lastOutcome: SessionRecoveryOutcome = 'unavailable'
let failures = 0
let breakerOpenedAt = 0

/** Test-only: clears the mutex, the throttle and the breaker. */
export function resetSessionRecovery(): void {
  inFlight = null
  lastAttemptAt = 0
  lastOutcome = 'unavailable'
  failures = 0
  breakerOpenedAt = 0
}

/** Counts one real failure and opens the breaker once MAX_FAILURES is reached. */
function recordFailure(): void {
  failures += 1
  if (failures >= MAX_FAILURES) {
    breakerOpenedAt = Date.now()
    logger.warn(`[sessionRecovery] breaker open for ${BREAKER_WINDOW_MS}ms after ${failures} failures`)
  }
}

async function runRefresh(): Promise<SessionRecoveryOutcome> {
  try {
    const outcome = await refreshTokenOutcome()
    if (outcome === 'recovered') {
      failures = 0
      breakerOpenedAt = 0
    } else if (outcome === 'rejected') {
      logger.warn('[sessionRecovery] token route reports no session')
      recordFailure()
    } else {
      logger.warn('[sessionRecovery] token route unavailable, session preserved')
    }
    return outcome
  } catch (err) {
    logger.error('[sessionRecovery] token request threw:', err)
    return 'unavailable'
  }
}

/**
 * Recovers the session, returning what the attempt established.
 *
 * See `recoverSession()` for the boolean form. Read this one only when the
 * answer decides whether to log a user out.
 */
export async function recoverSessionWithOutcome(): Promise<SessionRecoveryOutcome> {
  const now = Date.now()

  if (breakerOpenedAt && now - breakerOpenedAt < BREAKER_WINDOW_MS) return 'unavailable'
  if (breakerOpenedAt) { breakerOpenedAt = 0; failures = 0 }

  if (inFlight) return inFlight

  // Throttled: replay the verdict the last attempt reached instead of
  // inventing a failure. A refresh that succeeded 200 ms ago is still the
  // answer for the straggler 401 of a dependent query or a lazily mounted
  // tab, and nothing re-fires that query - the invalidation that used to
  // rescue it is exactly what this branch's Task 2 removed. Reporting failure
  // there surfaced an error on a page that had already recovered. The
  // network call itself stays rate limited: this branch never refreshes.
  if (now - lastAttemptAt < MIN_INTERVAL_MS) return lastOutcome

  lastAttemptAt = now
  inFlight = runRefresh()
    .then((outcome) => { lastOutcome = outcome; return outcome })
    .finally(() => { inFlight = null })
  return inFlight
}

export async function recoverSession(): Promise<boolean> {
  return (await recoverSessionWithOutcome()) === 'recovered'
}
